import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ENGINE_LABELS,
  PERSONA_LABELS,
  PERSONAS,
  type Engine,
} from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { KpiBar, type KpiPayload } from "../components/dashboard/KpiBar";
import {
  AlignmentMatrix,
  type MatrixPayload,
} from "../components/dashboard/AlignmentMatrix";
import {
  EngineRadar,
  type EngineBreakdown,
} from "../components/dashboard/EngineRadar";
import {
  PersonaSentiment,
  type PersonaRow,
} from "../components/dashboard/PersonaSentiment";
import { DriftRadar, type RadarFlag } from "../components/dashboard/DriftRadar";
import { ExportButton } from "../components/dashboard/ExportButton";
import "./dashboard.css";
import "./drift.css";

/**
 * FR-DASH-01/02/06 — the screen a Comms Manager opens every morning
 * (PRODUCT_FLOW.md Stage 5 §1).
 *
 * Filters live in the URL, not component state. FR-DASH-06's AC asks for
 * "persist across dashboard components within a session and shareable via
 * URL", and the URL satisfies both at once — a link pasted into Slack
 * reproduces exactly what the sender was looking at, which is how a drift
 * conversation actually starts.
 */

const WINDOWS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last quarter" },
  { days: 365, label: "Last year" },
];

interface Pillar {
  pillarId: string;
  name: string;
}

export function ExecutiveDashboard() {
  const { activeBrand } = useAuth();
  const brandId = activeBrand?.brandId ?? null;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [kpis, setKpis] = useState<KpiPayload | null>(null);
  const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
  const [breakdown, setBreakdown] = useState<EngineBreakdown | null>(null);
  const [personas, setPersonas] = useState<PersonaRow[] | null>(null);
  const [flags, setFlags] = useState<RadarFlag[] | null>(null);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [error, setError] = useState<string | null>(null);

  const days = Number(params.get("days") ?? 30);
  const pillarId = params.get("pillarId") ?? "";
  const engine = params.get("engine") ?? "";
  const persona = params.get("persona") ?? "";
  const metric = (params.get("metric") as "ndi" | "mptr") ?? "ndi";

  const query = useMemo(() => {
    const q = new URLSearchParams({ days: String(days) });
    if (pillarId) q.set("pillarId", pillarId);
    if (engine) q.set("engine", engine);
    if (persona) q.set("persona", persona);
    return q.toString();
  }, [days, pillarId, engine, persona]);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!brandId) return;
    setKpis(null);
    setMatrix(null);
    setError(null);

    // Loaded independently so one slow query doesn't blank the other panel —
    // DESIGN_SYSTEM.md §4's skeleton rule is per-component for this reason.
    void api<KpiPayload>(`/brands/${brandId}/dashboard/kpis?${query}`)
      .then(setKpis)
      .catch((err) =>
        setError(
          err instanceof ApiRequestError ? err.message : "Could not load KPIs.",
        ),
      );
    void api<MatrixPayload>(
      `/brands/${brandId}/dashboard/alignment-matrix?${query}`,
    )
      .then(setMatrix)
      .catch((err) =>
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Could not load the matrix.",
        ),
      );
    void api<EngineBreakdown>(
      `/brands/${brandId}/dashboard/engine-breakdown?${query}`,
    )
      .then(setBreakdown)
      .catch(() => setBreakdown({ axes: [], insight: null }));
    void api<{ personas: PersonaRow[] }>(
      `/brands/${brandId}/dashboard/persona-sentiment?${query}`,
    )
      .then((d) => setPersonas(d.personas))
      .catch(() => setPersonas([]));

    // The radar cards read the same flag list the Drift screen does, filtered
    // client-side to the active slice — one source, so a card and its
    // drill-down can never show different findings.
    void api<{ items: RadarFlag[] }>(`/brands/${brandId}/drift-flags?limit=200`)
      .then((d) =>
        setFlags(
          d.items.filter(
            (f) =>
              (!pillarId || f.pillarId === pillarId) &&
              (!engine || f.engine === engine),
          ),
        ),
      )
      .catch(() => setFlags([]));
  }, [brandId, query, pillarId, engine]);

  useEffect(() => {
    if (!brandId) return;
    void api<{ items: Pillar[] }>(`/brands/${brandId}/pillars`)
      .then((d) => setPillars(d.items))
      .catch(() => setPillars([]));
  }, [brandId]);

  const engines = matrix?.engines ?? [];

  const pillarName = useMemo(() => {
    const map = new Map(pillars.map((p) => [p.pillarId, p.name]));
    return (id: string) => map.get(id) ?? "Unknown pillar";
  }, [pillars]);

  // FR-DASH-01's AC — every KPI deep-links to the detail behind it, carrying
  // the active filters so the drill-down shows the same slice.
  const kpiTarget = useCallback(
    (key: string) => {
      const q = new URLSearchParams();
      if (pillarId) q.set("pillarId", pillarId);
      if (engine) q.set("engine", engine);
      if (key === "risks") q.set("severity", "critical");
      const suffix = q.toString();
      return key === "mptr" || key === "health"
        ? `/drift${suffix ? `?${suffix}` : ""}`
        : `/drift${suffix ? `?${suffix}` : ""}`;
    },
    [pillarId, engine],
  );

  return (
    <div className="stack">
      <header className="page__header">
        <h1 className="page__title">{activeBrand?.brandName}</h1>
        <p className="page__subtitle">
          What the AI engines are telling the world about you, and how far that
          has drifted from what you actually said.
        </p>
      </header>

      {error ? <div className="alert alert--error">{error}</div> : null}

      <div className="filterbar">
        <label className="filterbar__group">
          <span>Period</span>
          <select
            value={days}
            onChange={(e) => setFilter("days", e.target.value)}
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                {w.label}
              </option>
            ))}
          </select>
        </label>

        <label className="filterbar__group">
          <span>Narrative Pillar</span>
          <select
            value={pillarId}
            onChange={(e) => setFilter("pillarId", e.target.value)}
          >
            <option value="">All pillars</option>
            {pillars.map((p) => (
              <option key={p.pillarId} value={p.pillarId}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="filterbar__group">
          <span>Engine</span>
          <select
            value={engine}
            onChange={(e) => setFilter("engine", e.target.value)}
          >
            <option value="">All engines</option>
            {engines.map((e) => (
              <option key={e} value={e}>
                {ENGINE_LABELS[e] ?? e}
              </option>
            ))}
          </select>
        </label>

        <label className="filterbar__group">
          <span>Persona</span>
          <select
            value={persona}
            onChange={(e) => setFilter("persona", e.target.value)}
          >
            <option value="">All personas</option>
            {PERSONAS.map((p) => (
              <option key={p} value={p}>
                {PERSONA_LABELS[p]}
              </option>
            ))}
          </select>
        </label>

        <span className="filterbar__spacer" />
        <div className="exportbar">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            Reset filters
          </button>
          {/* FR-DASH-07 — exports the view as filtered, not a fixed default. */}
          <ExportButton
            brandId={brandId}
            filters={{ days, pillarId, engine, persona }}
          />
        </div>
      </div>

      <KpiBar data={kpis} to={kpiTarget} />

      {kpis && !kpis.hasData ? (
        // Honest empty state rather than a grid of zeroes — PRODUCT_FLOW.md's
        // cross-cutting states section, and the reason the KPI cards above read
        // "Not yet measured" instead of 0.
        <div className="alert alert--info">
          No scored responses in this window. Run a capture on an approved
          prompt, or widen the period, to see live numbers here.
        </div>
      ) : null}

      <section className="section">
        <div className="section__head">
          <div>
            <h2 className="section__title">Narrative Alignment Matrix</h2>
            <p className="section__lede">
              Each cell is one Narrative Pillar as one engine currently tells
              it. Click a cell for the prompts and responses behind the score.
            </p>
          </div>
          <label className="filterbar__group">
            <span>Show</span>
            <select
              value={metric}
              onChange={(e) => setFilter("metric", e.target.value)}
            >
              <option value="ndi">Drift (NDI)</option>
              <option value="mptr">Pull-through (MPTR)</option>
            </select>
          </label>
        </div>

        <AlignmentMatrix
          data={matrix}
          metric={metric}
          onCell={(cellPillarId, cellEngine: Engine) =>
            navigate(`/drift?pillarId=${cellPillarId}&engine=${cellEngine}`)
          }
        />
      </section>

      <section className="section">
        <div className="section__head">
          <div>
            <h2 className="section__title">Strategic Narrative Drift Radar</h2>
            <p className="section__lede">
              The highest-severity findings in this period, worst first — what
              was said, what your Ground Truth says instead, and why it
              happened.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => navigate("/drift")}
          >
            All findings
          </button>
        </div>
        <DriftRadar flags={flags} pillarName={pillarName} />
      </section>

      <section className="section">
        <div className="section__head">
          <div>
            <h2 className="section__title">Multi-Engine Narrative Health</h2>
            <p className="section__lede">
              How each engine compares on accuracy, message pull-through,
              framing, and the authority of the sources it relies on.
            </p>
          </div>
        </div>
        <EngineRadar data={breakdown} />
      </section>

      <section className="section">
        <div className="section__head">
          <div>
            <h2 className="section__title">Stakeholder Persona View</h2>
            <p className="section__lede">
              How well each stakeholder&rsquo;s questions are being answered.
              Click a persona for the prompts behind it.
            </p>
          </div>
        </div>
        <PersonaSentiment data={personas} pillarId={pillarId} />
      </section>
    </div>
  );
}
